import { useState, type FormEvent } from 'react';

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();

    const formData = new FormData(form);
    const honeypot = String(formData.get('website_alt') ?? '').trim();

    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    const message = String(formData.get('message') ?? '');
    if (message.length > 500) {
      setStatus('error');
      setFormError('Message cannot exceed 500 characters.');
      return;
    }

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9ih46koq4dpg2lrnbtg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      const serverMsg = parsed?.meta?.message ?? parsed?.meta?.detail ?? '';

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
        return;
      }

      setStatus('error');
      setFormError(
        serverMsg && !serverMsg.toLowerCase().includes('spam')
          ? serverMsg
          : 'Something went wrong. Please try again or email us directly.'
      );
    } catch {
      setStatus('error');
      setFormError('Network error. Please try again.');
    }
  };

  return (
    <section id="contact" className="relative bg-background-100 py-20 md:py-28">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14">
          <div className="lg:col-span-2">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold uppercase tracking-wider">
              <i className="ri-mail-send-line"></i>
              Contact
            </div>
            <h4 className="mt-5 font-heading font-bold text-3xl md:text-5xl text-foreground-900 leading-tight">
              <a href="#contact" className="hover:text-primary-700 transition-colors cursor-pointer">
                Let's build the next edge of your business.
              </a>
            </h4>
            <p className="mt-5 text-base text-foreground-600 leading-relaxed">
              Tell us about your project or the product you'd like a demo of. A senior
              solutions architect gets back within 1 business day.
            </p>

            <div className="mt-8 space-y-4">
              {[
                { icon: 'ri-map-pin-2-line', label: 'Yerevan, Armenia', sub: 'Global HQ' },
                { icon: 'ri-map-pin-2-line', label: 'Mohali, Chandigarh, India', sub: 'Delivery Centre' },
                { icon: 'ri-mail-line', label: 'tokenization@skyedge.am', sub: 'Business enquiries' },
                { icon: 'ri-links-line', label: 'gdbcloud.com', sub: 'Our data centre' },
              ].map((c) => (
                <div key={c.label} className="flex items-start gap-4 p-4 rounded-md bg-background-50 border border-background-200">
                  <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-500 text-background-50 flex-shrink-0">
                    <i className={`${c.icon} text-lg`}></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-900">{c.label}</div>
                    <div className="text-xs text-foreground-500 mt-0.5">{c.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3">
            <form
              id="skyedge-contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="relative p-6 md:p-8 rounded-lg bg-background-50 border border-background-200"
            >
              {/* Honeypot */}
              <input
                type="text"
                name="website_alt"
                className="field-alt-website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground-800 mb-1.5 uppercase tracking-wider">Full name *</label>
                  <input
                    type="text"
                    name="fullName"
                    required
                    className="w-full px-4 py-3 text-sm rounded-md bg-background-100 border border-background-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 text-foreground-900"
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-800 mb-1.5 uppercase tracking-wider">Work email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 text-sm rounded-md bg-background-100 border border-background-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 text-foreground-900"
                    placeholder="jane@company.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-800 mb-1.5 uppercase tracking-wider">Company</label>
                  <input
                    type="text"
                    name="company"
                    className="w-full px-4 py-3 text-sm rounded-md bg-background-100 border border-background-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 text-foreground-900"
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-800 mb-1.5 uppercase tracking-wider">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    className="w-full px-4 py-3 text-sm rounded-md bg-background-100 border border-background-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 text-foreground-900"
                    placeholder="+374 · +91 · ..."
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-foreground-800 mb-1.5 uppercase tracking-wider">I'm interested in *</label>
                  <select
                    name="interest"
                    required
                    defaultValue=""
                    className="w-full px-4 py-3 text-sm rounded-md bg-background-100 border border-background-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 text-foreground-900 cursor-pointer pr-10"
                  >
                    <option value="" disabled>Select a service or product</option>
                    <option value="Custom Software">Custom Software Development</option>
                    <option value="GDB Data Centre">GDB Data Centre / Colocation</option>
                    <option value="Cloud Services">Cloud Services</option>
                    <option value="AI Agentic Tools">AI Agentic Tools</option>
                    <option value="Omni AI">Omni AI Platform</option>
                    <option value="RWA Tokenization">Real Estate Tokenization</option>
                    <option value="Partnership">Partnership / Other</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-foreground-800 mb-1.5 uppercase tracking-wider">Message *</label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 text-sm rounded-md bg-background-100 border border-background-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 text-foreground-900 resize-none"
                    placeholder="Tell us a bit about your project or the product you'd like a demo of..."
                  />
                  <div className="text-xs text-foreground-500 mt-1">Up to 500 characters.</div>
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <p className="text-xs text-foreground-500">
                  By submitting you agree to our privacy policy. No spam, ever.
                </p>
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="px-6 py-3 rounded-md bg-accent-500 hover:bg-accent-600 disabled:opacity-60 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors inline-flex items-center justify-center gap-2"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i> Sending...
                    </>
                  ) : (
                    <>
                      Send message <i className="ri-send-plane-line"></i>
                    </>
                  )}
                </button>
              </div>

              {status === 'success' && (
                <div className="mt-4 p-3 rounded-md bg-secondary-100 border border-secondary-200 text-secondary-900 text-sm flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-lg"></i>
                  Thanks — your message is in. We'll reply within 1 business day.
                </div>
              )}
              {status === 'error' && formError && (
                <div className="mt-4 p-3 rounded-md bg-accent-100 border border-accent-200 text-accent-900 text-sm flex items-center gap-2">
                  <i className="ri-error-warning-line text-lg"></i>
                  {formError}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}